import React from 'react';
import { Input, Loading, Select } from '@avila-tek/ui/src';
import { Hit } from '@algolia/client-search';
import { useRouter } from 'next/router';
import {
  Category,
  Company,
  PageInfo,
  Pagination,
  Product,
} from '../../../models';
import { useAlgoliaSearch } from '../../../hooks/useAlgoliaSearch';
import { AlgoliaHitToObject } from '../../../lib';
import Layout from '../../layout/Layout';
import { Marca, PriceFilter } from '../../category';
import ProductsCatalog from './ProductsCatalog';

interface CategoryCatalogProps {
  loading: boolean;
  category: Category;
  data: Pagination<Product>;
  max: string;
  setMax: React.Dispatch<React.SetStateAction<string>>;
  setMaxPrice: React.Dispatch<React.SetStateAction<number>>;
  min: string;
  setMin: React.Dispatch<React.SetStateAction<string>>;
  setMinPrice: React.Dispatch<React.SetStateAction<number>>;
  minPrice: number;
  maxPrice: number;
  allies: Company[];
  allyId: string;
  setAllyId: React.Dispatch<React.SetStateAction<string>>;
}

function CategoryCatalog({
  loading,
  category,
  data,
  max,
  setMax,
  setMaxPrice,
  min,
  setMin,
  setMinPrice,
  minPrice,
  maxPrice,
  allies,
  allyId,
  setAllyId,
}: CategoryCatalogProps) {
  const router = useRouter();
  const [orderBy, setOrderBy] = React.useState('priority');
  const [searchText, setSearchText] = React.useState<string | number>('');
  const [productsData, setProductsData] = React.useState<Product[]>(
    data?.items
  );
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<Product>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'category',
      'category.name',
      'company',
      'company.name',
      'title',
      'description',
    ],
    index: 'products',
    filters: `category._id:${router?.query?.categoryId} ${
      allyId && `AND company._id:${allyId}`
    }`,
  });

  React.useEffect(() => {
    if (data && !loading) {
      setProductsData(data?.items);
    }
  }, [data, loading]);

  React.useEffect(() => {
    async function syncData() {
      if (searchText.toString()?.length > 0) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        if (searchText.toString()?.length > 0) {
          setProductsData(AlgoliaHitToObject<Product>(items as Hit<Product>[]));
          setPageInfoAlgolia(pageInfo);
        } else {
          setProductsData(data?.items);
        }
      }
    }
    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pageInfoAlgolia.currentPage, searchText]);

  const nextPage = () => {
    if (pageInfoAlgolia?.hasNextPage) {
      setPageInfoAlgolia((prev) => ({
        ...prev,
        currentPage: prev.currentPage + 1,
      }));
    }
  };

  const previousPage = () => {
    if (pageInfoAlgolia?.hasPreviousPage) {
      setPageInfoAlgolia((prev) => ({
        ...prev,
        currentPage: prev.currentPage - 1,
      }));
    }
  };
  const routesHistory = [
    {
      text: 'Catálogo',
      href: '/app/catalogue',
    },
    {
      text: 'Categoría',
      href: router.asPath,
    },
  ];
  return (
    <Layout sectionTitle="Categoría" routesHistory={routesHistory}>
      <div className="w-full flex flex-col lg:flex-row bg-no-repeat bg-bottom bg-meitBoxes">
        <div className="hidden lg:flex flex-col w-full lg:w-1/4">
          <div className="flex flex-col items-start justify-start">
            <Marca allies={allies} allyId={allyId} setAllyId={setAllyId} />
          </div>
        </div>
        <div className="w-full lg:w-3/4 flex flex-col my-10 gap-8 px-10">
          <div className="flex flex-row">
            <div className="text-color-primary-text-300 font-semibold text-2xl">
              {category?.name}
            </div>
          </div>
          <div className="w-full flex flex-col items-end justify-end gap-5">
            <div className="w-full flex flex-row flex-wrap gap-5 items-center justify-start lg:justify-between">
              <div className="w-full lg:w-[400px]">
                <Input
                  type="text"
                  label="Filtrar"
                  placeholder="Buscar..."
                  name="search"
                  value={searchText}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearchText(e.target.value);
                  }}
                  className="placeholder-gray-500 bg-system-100 focus:outline-none focus-within:ring-0 border-0"
                />
              </div>
              <div>
                <PriceFilter
                  max={max}
                  setMax={setMax}
                  setMaxPrice={setMaxPrice}
                  min={min}
                  setMin={setMin}
                  setMinPrice={setMinPrice}
                  loading={loading}
                />
              </div>
            </div>
            <Select
              name="orderBy"
              id="orderBy"
              value={orderBy}
              onChange={(e) => {
                e.preventDefault();
                setOrderBy(e.target.value);
              }}
              className="cursor-pointer focus:outline-none focus-within:ring-0 pr-10 pl-2 text-xs"
            >
              <option id="priority" value="priority">
                Ordenar por popularidad
              </option>
            </Select>
          </div>
          <div className="flex lg:hidden flex-col items-start justify-start lg:px-10 gap-5">
            <Marca allies={allies} allyId={allyId} setAllyId={setAllyId} />
          </div>
          {loading || !data ? (
            <Loading />
          ) : (
            <ProductsCatalog
              products={productsData}
              pageInfo={pageInfoAlgolia}
              setPageInfo={setPageInfoAlgolia}
              minPrice={minPrice}
              maxPrice={maxPrice}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

export default CategoryCatalog;
